// @flow

import { ErrorActions } from '@zapier/toolbox-redux-framework';

import * as CliAppInvitee from 'app/entities/CliAppInvitee';
import { notify } from 'app/developer-v3/store/shared/actions';
import { NEW_ENTITY_IDS, ROLES } from 'app/developer-v3/constants';

import ActionTypes from './ActionTypes';

import type { Dispatch, GetState } from 'app/common/types';

type MemberProps = {
  appVersion?: string,
  email: string,
  id?: string,
  message?: string,
};

export const submitNewTeamMember = (props?: {}) => {
  return { type: ActionTypes.SUBMIT_NEW_TEAM_MEMBER, teamMember: props };
};

export const submitNewTeamMemberFail = (error: string) => {
  return { type: ActionTypes.SUBMIT_NEW_TEAM_MEMBER_FAIL, error };
};

export const updateAddForm = (role: $Keys<typeof ROLES>, value: string) => {
  const actionType =
    role === ROLES.admin
      ? ActionTypes.UPDATE_CLI_ADD_ADMIN_FORM
      : ActionTypes.UPDATE_CLI_ADD_INVITEE_FORM;

  return {
    type: actionType,
    fieldName: 'email',
    value,
  };
};

export const addEntity = (
  dispatch: Dispatch,
  getState: GetState,
  entity,
  entityProps: MemberProps,
  role: $Keys<typeof ROLES>
) => {
  dispatch(submitNewTeamMember());

  const entityId =
    role === ROLES.admin ? NEW_ENTITY_IDS.admin : NEW_ENTITY_IDS.invitee;

  const finalEntityProps = {
    ...entityProps,
    role,
    status: 'waiting',
    id: entityId,
  };

  dispatch(
    entity.actions.createCollectionEntity(finalEntityProps, {
      isPersisted: false,
    })
  );

  dispatch(entity.actions.createEntity(null, entityId)).then(() => {
    const state = getState();
    const createdEntity = entity.selectors.all.entity(entityId, state);
    const isFailure = entity.selectors.isFailure(createdEntity);

    if (isFailure) {
      const statusMessage = entity.selectors.statusMessage(createdEntity);

      dispatch(submitNewTeamMemberFail(statusMessage));
      return dispatch(ErrorActions.clearErrors());
    }
    dispatch(updateAddForm(role, ''));
    dispatch({ type: ActionTypes.RESET_NEW_TEAM_MEMBER_FORM });
    return dispatch(
      notify.success(`Invitation email sent to ${entityProps.email}.`)
    );
  });
};

export const addCliInvitee = (
  appId: string | number,
  email: string,
  appVersion: string
) => {
  return (dispatch: Dispatch, getState: GetState) =>
    addEntity(
      dispatch,
      getState,
      CliAppInvitee,
      { email, app_version: appVersion },
      ROLES.invitee
    );
};

// For invitees, admins, and subscribers
export const deleteEntity = (entity, entityProps: MemberProps) => (
  dispatch: Dispatch,
  getState: GetState
) =>
  dispatch(entity.actions.deleteEntity(null, entityProps.id)).then(() => {
    const state = getState();
    const fetchedEntity = entity.selectors.all.entity(entityProps.id, state);
    const isFailure = entity.selectors.isFailure(fetchedEntity);
    const isNotFound = entity.selectors.isNotFound(fetchedEntity);

    // Only notify of failure if it failed
    if (isFailure || isNotFound) {
      const statusMessage = entity.selectors.statusMessage(fetchedEntity);
      return dispatch(
        notify.failure(
          statusMessage || 'An error has occurred. Please try again'
        )
      );
    }
    return dispatch(
      notify.success(`${entityProps.email} has been removed from your team.`)
    );
  });

export const deleteCliInvitee = (
  appId: string | number,
  inviteeProps: MemberProps
) => deleteEntity(CliAppInvitee, inviteeProps);

export const loadCliInvitees = () => {
  return (dispatch: Dispatch) => {
    dispatch(CliAppInvitee.actions.resetCollection());
    dispatch(CliAppInvitee.actions.loadCollection());
  };
};

export const clearMessage = (id: string) => ({
  type: ActionTypes.CLEAR_MESSAGE,
  payload: { id },
});

export const resetNewTeamMemberForm = () => ({
  type: ActionTypes.RESET_NEW_TEAM_MEMBER_FORM,
});

export const updateNewTeamMember = (values: {}) => {
  return { type: ActionTypes.UPDATE_NEW_TEAM_MEMBER, values };
};
